import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ExternalLink } from 'lucide-react';
import Header from '../components/Header';
import parameterData from '../lib/parameterData';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { Separator } from '../components/ui/separator';
import { Badge } from '../components/ui/badge';

/**
 * Parametreler sayfası - Yangın sınıfı parametrelerini listeler
 * @returns {JSX.Element} Parametreler sayfası
 */
const Parameters = () => {
  const { t, i18n } = useTranslation();
  
  // Dil durumuna göre parametre ismini döndüren yardımcı fonksiyon
  const getParameterName = (parameter) => {
    return i18n.language === 'en' && parameter.material_en 
      ? parameter.material_en 
      : parameter.material;
  };
  
  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header />
      
      <main className="flex-1 container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold tracking-tight mb-2">{t('parameters.title')}</h1>
          <p className="text-muted-foreground">{t('parameters.subtitle')}</p>
        </div>
        
        <section className="mb-10">
          <h2 className="text-2xl font-semibold mb-6">{t('parameters.fire_class_parameters')}</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6">
            {parameterData.map(parameter => (
              <Card key={parameter.id} className="overflow-hidden">
                <CardHeader className="pb-3">
                  <div className="flex justify-between items-center">
                    <CardTitle>
                      {t('parameters.parameter')} {parameter.id}
                    </CardTitle>
                    <Badge variant={parameter.fire_class === 'A' ? 'default' : 'destructive'}>
                      {t('parameters.fire_class')} {parameter.fire_class}
                    </Badge>
                  </div>
                  <CardDescription className="line-clamp-2">{getParameterName(parameter)}</CardDescription>
                </CardHeader>
                
                <CardContent className="pb-3">
                  <div className="space-y-4">
                    <div>
                      <h3 className="text-sm font-medium text-muted-foreground mb-2">{t('parameters.standards')}</h3>
                      <div className="grid grid-cols-1 gap-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">EN 15276-1:</span>
                          <span className="font-medium">{parameter.en_test_number}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">ISO 15779:</span>
                          <span className="font-medium">{parameter.iso_test_number}</span>
                        </div>
                      </div>
                    </div>
                    
                    <Separator />
                    
                    <div>
                      <h3 className="text-sm font-medium text-muted-foreground mb-2">{t('parameters.suppression_info')}</h3>
                      <div className="grid grid-cols-1 gap-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">{t('parameters.density')}:</span>
                          <span className="font-medium">{parameter.density} {parameter.density_unit}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
                
                <CardFooter className="flex justify-between pt-2">
                  <Badge variant="outline">{t('parameters.code')}: {parameter.code}</Badge>
                  <Badge variant="secondary">{t('parameters.id')}: {parameter.id}</Badge>
                </CardFooter>
              </Card>
            ))}
          </div>
        </section>
        
        <section className="mb-10">
          <h2 className="text-2xl font-semibold mb-4">{t('parameters.standards_info')}</h2>
          <Card>
            <CardHeader>
              <CardTitle>{t('parameters.en_15276')}</CardTitle>
              <CardDescription>{t('parameters.en_15276_description')}</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-4">
                {t('parameters.standard_explanation')}
              </p>
              <div className="space-y-2">
                <div className="flex items-center">
                  <div className="h-2 w-2 rounded-full bg-blue-500 mr-2"></div>
                  <p className="text-sm">{t('parameters.a_class_info')}</p>
                </div>
                <div className="flex items-center">
                  <div className="h-2 w-2 rounded-full bg-red-500 mr-2"></div>
                  <p className="text-sm">{t('parameters.b_class_info')}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>
        
        {/* Parametre Raporu ve Detaylı Analiz Bölümü */}
        <section className="mb-10">
          <h2 className="text-2xl font-semibold mb-6">{t('parameters.parameter_report')}</h2>
          
          <Card>
            <CardHeader>
              <CardTitle>{t('parameters.general_analysis')}</CardTitle>
              <CardDescription>{t('parameters.analysis_description')}</CardDescription>
            </CardHeader>
            
            <CardContent>
              <div className="space-y-4">
                <div className="space-y-2">
                  <h3 className="font-medium">{t('parameters.parameter_scope')}</h3>
                  <p className="text-sm text-muted-foreground">
                    {t('parameters.parameter_scope_description')}
                  </p>
                </div>
                
                <Separator />
                
                <div className="space-y-2">
                  <h3 className="font-medium">{t('parameters.standards_section')}</h3>
                  <p className="text-sm text-muted-foreground">
                    {t('parameters.standards_section_description')}
                  </p>
                </div>
                
                <Separator />
                
                <div className="space-y-2">
                  <h3 className="font-medium">{t('parameters.suppression_density')}</h3>
                  <p className="text-sm text-muted-foreground">
                    {t('parameters.suppression_density_description')}
                  </p>
                </div>
                
                <Separator />
                
                <div className="space-y-2">
                  <h3 className="font-medium">{t('parameters.fire_class_distribution')}</h3>
                  <div className="space-y-2">
                    <p className="text-sm text-muted-foreground">
                      {t('parameters.fire_class_distribution_description')}
                    </p>
                  </div>
                </div>
                
                <Separator />
                
                <div className="space-y-2">
                  <h3 className="font-medium">{t('parameters.density_comparison')}</h3>
                  <p className="text-sm text-muted-foreground">
                    {t('parameters.density_comparison_description')}
                  </p>
                </div>
                
                <Separator />
                
                <div className="space-y-2">
                  <h3 className="font-medium">{t('parameters.application_recommendation')}</h3>
                  <p className="text-sm text-muted-foreground">
                    {t('parameters.application_recommendation_description')}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>
      </main>
      
      <footer className="py-8 border-t bg-muted/20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="text-center md:text-left mb-4 md:mb-0">
              <p className="text-xs text-muted-foreground">© {new Date().getFullYear()} ADC Makine Sanayi ve Ticaret Limited Şirketi. Tüm hakları saklıdır.</p>
            </div>
            <div className="flex items-center gap-8">
              <Link 
                to="/privacy-policy" 
                className="text-sm text-muted-foreground hover:text-primary transition-colors"
              >
                {t('privacy.title')}
              </Link>
              <Link 
                to="/terms-of-service" 
                className="text-sm text-muted-foreground hover:text-primary transition-colors"
              >
                {t('terms.title')}
              </Link>
              <a 
                href="https://www.alasfire.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-sm text-primary hover:underline flex items-center"
              >
                <span>alasfire.com</span>
                <ExternalLink className="h-3 w-3 ml-1" />
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Parameters; 